/**
 * Reports API Service for PropWise
 *
 * Handles communication with the API Gateway endpoints for fetching
 * inspection reports and their details.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { getApiBaseUrl } from '../utils/amplifyConfig';
import { HTTP_STATUS, TIMEOUT } from '../constants';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

// Types
export type ReportStatus = 'processing' | 'completed' | 'failed' | 'pending';

export interface ReportSummary {
  id: string;
  propertyAddress: string;
  fileName: string;
  uploadDate: string;
  status: ReportStatus;
  agentEmail: string;
  /** Null until the processor scores the report (pending, processing, failed). */
  aiScore?: number | null;
  issues?: number;
  highCount?: number;
  mediumCount?: number;
  lowCount?: number;
  totalRepairCost?: number;
}

export interface Vendor {
  name: string;
  phone: string;
  website: string;
  /**
   * Ready-to-render Google URL (a Maps place URL when a place_id exists, else a
   * Google Search fallback). Optional for backward-compat with legacy reports
   * created before the API surfaced this field.
   */
  google_profile?: string;
}

/**
 * Whole-dollar Low / Expected / High from the API (task 07). `low === high` means
 * no band: a user override, or a tiny estimate (<= $3) whose 15% band rounds away.
 */
export interface CostRange {
  low: number;
  expected: number;
  high: number;
}

/** One entry of `summary.categoryTotals`. All-zero categories are omitted server-side. */
export interface CategoryCostTotal extends CostRange {
  category: string;
}

export interface CategorizedItem {
  itemId: string;
  itemText: string;
  category: string;
  severity: string;
  itemType?: 'defect' | 'limitation' | 'informational' | 'service' | 'needs_further_inspection';
  /**
   * Pipeline-computed estimated cost. `null` for contractor-eval items (the
   * pricing service deliberately defers to contractor evaluation and emits no
   * dollar — see backend `craftsman.pricing._set_contractor_eval`). Callers
   * that sum costs must guard for null (see `categoryUtils.ts` and
   * `ReportDetails.calculateShareTotal`).
   */
  estimatedCost: number | null;
  /** User's edited cost override. `null` or missing = no override. */
  editedCost?: number | null;
  /**
   * The model estimate as a range. `null` = contractor-eval (no price); `{0,0,0}` =
   * a non-defect or zero-cost item. Optional: missing on a pre-07 payload, in which
   * case `categoryUtils.itemCostRange` falls back to `estimatedCost` as a point range.
   */
  costRange?: CostRange | null;
  /** The user's override as a point range (low = expected = high). `null` = no override. */
  editedCostRange?: CostRange | null;
  /** User's edited category override. `null` or missing = no override. */
  editedCategory?: string | null;
  /** User's edited severity override. `null` or missing = no override. */
  editedSeverity?: string | null;
  /** User marked this item as excluded from the shared email. */
  excluded?: boolean;
  complianceFlag: boolean;
  /** `null` when compliance review never classified the item (unpriced, omitted, or a degraded review). */
  complianceType: string | null;
  complianceNotes: string[];
  vendors?: Vendor[];
  pageNumber?: number;
  /**
   * Craftsman pricing-signal taxonomy: 'minimum' | 'scoped' | 'no_match' | 'contractor_eval'.
   * `undefined` ONLY for legacy reports created before this field shipped — treat as "unknown" at render time.
   * `'contractor_eval'` accompanies `needsContractorEvaluation === true` and an `estimatedCost` of `null`.
   */
  pricingCategory?: 'minimum' | 'scoped' | 'no_match' | 'contractor_eval';
  /**
   * True when secondary unit-priced items are attached to this match.
   * Backend currently always returns `false` (the API formatter coerces). Treat `false` as
   * "unknown" for legacy reports until sibling task 3-Medium-scope-dependent-secondary-unit-pricing lands.
   */
  unitPricingAvailable?: boolean;
  /**
   * True when (category, scope) routed to an assembly path flagged contractor-eval.
   * Backend coerces missing → `false`; for legacy reports `false` means "unknown / not flagged."
   */
  needsContractorEvaluation?: boolean;
}

export interface ShareReportPayload {
  includeContractDetails?: boolean;
  includeComplianceFlags?: boolean;
}

export interface ShareReportResponse {
  message: string;
  reportId: string;
  itemsShared: number;
  totalCost: number;
  inspectionScore: number;
}

export interface BeginProcessingResponse {
  message: string;
  reportId: string;
  fileCount: number;
}

/** Contract metadata payload mirroring backend extract_contract_metadata shape. */
export interface ContractMetadataPayload {
  contract_acceptance_date?: string;
  inspection_window_days?: string;
  is_as_is?: string;
  repair_credit_amount?: string;
  seller_repair_credit_agreed?: string;
  single_defect_threshold?: string;
  category_threshold?: string;
}

/**
 * Public-facing payload for POST /reports — everything the form supplies.
 * `identity_id` is NOT part of this type because callers should never set it;
 * `createReport()` injects it internally from the Cognito session.
 */
export interface CreateReportPayload {
  address: string;
  city: string;
  state: string;
  zip: string;
  year: string;
  file_type: string;
  contract_metadata?: ContractMetadataPayload;
}

/**
 * Wire-format request body. Private to this module so callers cannot
 * construct a body with `identity_id` baked in — the only path to setting
 * `identity_id` is through `createReport()`, which sources it from the
 * authenticated session.
 */
type CreateReportRequestBody = CreateReportPayload & { identity_id: string };

/**
 * Response from POST /reports.
 *
 * The backend may return additional fields (the full row); the frontend
 * only consumes these two. Keep the type narrow on purpose — extending it
 * later is cheap; pretending we use server fields we don't is expensive.
 */
export interface CreateReportResponse {
  reportId: string;
  uploadPrefix: string;
}

/**
 * Canonical severities; mirrors backend shared.constants.VALID_SEVERITIES.
 * Ordered most→least severe (matches the severity dropdown option order).
 * Keep byte-identical to the backend tuple — the share carry-through path
 * validates against the backend copy.
 */
export const VALID_SEVERITIES = ['High', 'Medium', 'Low', 'Informational'] as const;
export type ValidSeverity = typeof VALID_SEVERITIES[number];

/**
 * Partial edit payload for PATCH /reports/{reportId}/items/{itemId}.
 *
 * Semantics (precise — be careful with undefined vs null):
 * - Omit a key entirely to leave that field unchanged on the server.
 * - Pass `editedCost: number` to SET the override.
 * - Pass `editedCost: null` to CLEAR (DynamoDB REMOVE) — restores "no edit" state.
 * - Pass `editedCategory: string` to SET; `null` to CLEAR; omit to leave unchanged.
 * - Pass `editedSeverity: string` (one of VALID_SEVERITIES) to SET; `null` to
 *   CLEAR; omit to leave unchanged.
 * - Pass `excluded: boolean` to SET; omit to leave unchanged.
 *
 * Do NOT pass `undefined` explicitly — JSON.stringify drops undefined keys,
 * which has the same effect as omission, but is confusing to read. Build the
 * object with conditional spread if you only sometimes include a field.
 */
export interface ItemEditPayload {
  editedCost?: number | null;
  editedCategory?: string | null;
  editedSeverity?: string | null;
  excluded?: boolean;
}

export interface ItemEditResponse {
  reportId: string;
  item: CategorizedItem;
}

export interface BulkUpdateItemsExcludedPayload {
  itemIds: string[];
  excluded: boolean;
}

export interface BulkUpdateItemsExcludedResponse {
  reportId: string;
  items: CategorizedItem[];
  updatedCount: number;
}

export interface NegotiationStrategy {
  label: string;
  summary: string;
  recommended_ask?: string;
  key_points: string[];
  risk_level: 'low' | 'medium' | 'high';
}

export interface ReportSummaryStats {
  highCount: number;
  mediumCount: number;
  lowCount: number;
  totalItems: number;
  totalRepairCost?: number;
  /** Server-side range total over all items (ignores overrides; the page re-sums instead). */
  totalRepairCostRange?: CostRange;
  categoryTotals?: CategoryCostTotal[];
}

export interface ReportFile {
  /** Original PDF filename (basename only, no path). */
  name: string;
  /** Presigned GET URL — TTL ~1h, set as `attachment` so the browser saves. */
  downloadUrl: string;
  /** Organized-bucket key. */
  s3Key: string;
}

export interface ReportDetails {
  id: string;
  propertyAddress: string;
  city: string;
  state: string;
  zip: string;
  fileName: string;
  uploadDate: string;
  status: ReportStatus;
  processingStage?: string;
  errorMessage?: string;
  /** Soft warning shown on a completed report when automated pricing failed (all defects $0). */
  pricingWarning?: string;
  /** Soft warning shown on a completed report when a non-essential late stage
   *  (strategy summary or vendor matching) failed; the report is otherwise complete. */
  completionWarning?: string;
  /** Null until the processor scores the report. */
  aiScore?: number | null;
  totalRepairCost?: number;
  items: CategorizedItem[];
  negotiationOptions?: NegotiationStrategy[];
  summary?: ReportSummaryStats;
  /** Multi-file: one entry per uploaded PDF with its own presigned URL. */
  files?: ReportFile[];
  /** Contract terms captured at upload; carries the single-defect cost threshold. */
  contractMetadata?: ContractMetadataPayload;
}

/** One purchasable price, from the per-environment allowlist. */
export interface CatalogEntry {
  priceId: string;
  /** Display label; falls back to the price id when the catalog has none. */
  label: string;
  kind: 'one_time' | 'subscription';
}

/** Runs grouped by when they expire and where they came from. */
export interface AccountExpiry {
  /** ISO-Z, or null for grants that never expire. */
  expiresAt: string | null;
  runs: number;
  source: 'subscription' | 'purchase' | 'promo' | 'beta' | 'unknown';
}

/**
 * GET /account.
 *
 * subscriptionRuns and purchasedCredits are deliberately two figures with no
 * combined total, here and on the server: they expire on different schedules
 * and are spent in a defined order, so one number would be a number the user
 * cannot reason about.
 *
 * Every count is an integer; the server casts before serialising.
 */
export interface AccountSummary {
  planName: string;
  subscriptionRuns: number;
  purchasedCredits: number;
  /** Soonest first, with undated grants last. */
  expiries: AccountExpiry[];
  betaEligible: boolean;
  hasActiveSubscription: boolean;
  /**
   * ISO date a scheduled cancellation takes effect, or null.
   *
   * Cancelling at period end leaves the subscription active, so this is the
   * only field that changes. Runs stay spendable until this date.
   */
  subscriptionCancelsAt: string | null;
  /** Empty when nothing is purchasable in this environment. */
  catalog: CatalogEntry[];
}

// API Error class
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    /** Parsed Retry-After, in ms. Set on 429s from the API Gateway throttle. */
    public retryAfterMs?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Parse Retry-After (delta-seconds or HTTP-date) into ms, or undefined. */
function parseRetryAfterMs(value: string | null): number | undefined {
  if (!value) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(value);
  return Number.isNaN(date) ? undefined : Math.max(0, date - Date.now());
}

/**
 * Get the authentication token from Cognito session.
 * @returns ID token string
 * @throws ApiError if not authenticated
 */
async function getAuthToken(): Promise<string> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
      throw new ApiError('Not authenticated', HTTP_STATUS.UNAUTHORIZED, 'NO_TOKEN');
    }
    return token;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError('Authentication failed', HTTP_STATUS.UNAUTHORIZED, 'AUTH_FAILED');
  }
}

interface ApiRequestOptions extends Omit<RequestInit, 'signal'> {
  timeout?: number;
}

/**
 * Make an authenticated API request.
 * @param endpoint - API endpoint path (without base URL)
 * @param options - Fetch options including optional timeout
 * @returns Parsed JSON response
 * @throws ApiError on failure
 * @throws TimeoutError if request exceeds timeout
 */
async function apiRequest<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
  const token = await getAuthToken();
  const apiBase = getApiBaseUrl();

  if (!apiBase) {
    throw new ApiError('API base URL not configured', HTTP_STATUS.INTERNAL_SERVER_ERROR, 'NO_API_URL');
  }

  const url = `${apiBase}${endpoint}`;
  const { timeout, ...fetchOptions } = options;

  const response = await fetchWithTimeout(
    url,
    {
      ...fetchOptions,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
      timeout,
    }
  );

  if (!response.ok) {
    let errorMessage = `Request failed: ${response.status}`;
    let errorCode: string | undefined;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
      errorCode = errorData.code;
    } catch {
      // Use default error message
    }
    const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'));
    throw new ApiError(errorMessage, response.status, errorCode, retryAfterMs);
  }

  return response.json();
}

/**
 * Fetch all reports for the authenticated user.
 * @returns List of report summaries
 */
export async function fetchReports(): Promise<ReportSummary[]> {
  const data = await apiRequest<{ reports: ReportSummary[] }>('/reports');
  return data.reports;
}

/**
 * Fetch the signed-in user's plan, balance and purchasable catalog.
 *
 * NOT unwrapped, unlike every other endpoint here. /reports returns
 * `{reports}` and /reports/{id} returns `{report}`, but /account serialises
 * the summary at the top level.
 *
 * @returns Plan name, the two balance figures, expiries and the catalog
 */
export async function fetchAccount(): Promise<AccountSummary> {
  return apiRequest<AccountSummary>('/account');
}

/**
 * Fetch full details for a single report.
 * @param reportId - Report UUID
 * @param timeout - Optional timeout in milliseconds (for polling use cases)
 * @returns Complete report details
 */
export async function fetchReportDetails(
  reportId: string,
  timeout?: number
): Promise<ReportDetails> {
  const encodedId = encodeURIComponent(reportId);
  const data = await apiRequest<{ report: ReportDetails }>(
    `/reports/${encodedId}`,
    { timeout }
  );
  return data.report;
}

/**
 * Share a filtered report via email.
 * Sends the inspection report email with the user-selected items and any
 * cost edits applied. The email goes to the agent's own account, matching
 * the workflow of the old automatic post-processing send.
 *
 * @param reportId - Report UUID
 * @param payload - Selected item IDs and cost edits
 * @returns Share confirmation with summary
 */
export async function shareReport(
  reportId: string,
  payload: ShareReportPayload
): Promise<ShareReportResponse> {
  const encodedId = encodeURIComponent(reportId);
  return apiRequest<ShareReportResponse>(
    `/reports/${encodedId}/share`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}

export interface PortalSessionResponse {
  url: string;
}

/**
 * POST /account/portal-session.
 *
 * The provider's customer portal covers cancellation, payment methods and
 * invoices. It does NOT change plans.
 *
 * The URL carries a short-lived token and is never stored: fetched on click,
 * navigated to immediately, discarded.
 *
 * Fails with an ApiError carrying NO_CUSTOMER (404), PORTAL_FAILED (502) or
 * PORTAL_UNAVAILABLE (503). The codes matter, because the generic 404 copy is
 * report-centric.
 */
export async function createPortalSession(): Promise<PortalSessionResponse> {
  return apiRequest<PortalSessionResponse>('/account/portal-session', {
    method: 'POST',
  });
}

export interface TermsStatusResponse {
  acceptanceRequired: boolean;
}

/** Throws unless the body carries a real boolean. A missing flag must never
 * read as "nothing required": the caller treats a throw as "couldn't check". */
function assertTermsStatus(result: TermsStatusResponse): TermsStatusResponse {
  if (typeof result?.acceptanceRequired !== 'boolean') {
    throw new ApiError(
      'Malformed terms status response',
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
      'MALFORMED_RESPONSE',
    );
  }
  return result;
}

/**
 * GET /account/terms. Whether the signed-in user must accept the current
 * Terms and Privacy Policy. The backend owns the version; it never reaches
 * the client.
 *
 * Fails with an ApiError carrying TERMS_UNAVAILABLE (503) or
 * TERMS_READ_FAILED (500). Short timeout: this sits on the path to first
 * paint at every sign-in and page load.
 */
export async function fetchTermsStatus(): Promise<TermsStatusResponse> {
  return assertTermsStatus(
    await apiRequest<TermsStatusResponse>('/account/terms', {
      timeout: TIMEOUT.GATE_CHECK_MS,
    }),
  );
}

/**
 * POST /account/terms. Records acceptance of the current version. Sends no
 * body: the server stamps version and time itself.
 *
 * Fails with an ApiError carrying TERMS_UNAVAILABLE (503) or
 * TERMS_WRITE_FAILED (500).
 */
export async function recordTermsAcceptance(): Promise<TermsStatusResponse> {
  return assertTermsStatus(
    await apiRequest<TermsStatusResponse>('/account/terms', { method: 'POST' }),
  );
}

/**
 * Persist a per-item user edit (edited cost and/or exclude flag).
 * Idempotent: same body → same resulting state.
 *
 * @param reportId  Report UUID
 * @param itemId    Item identifier (sort key on ReportItems)
 * @param edit      Partial edit payload; include only the fields to change.
 * @returns The updated item as it now lives in DynamoDB.
 */
export async function updateReportItemEdit(
  reportId: string,
  itemId: string,
  edit: ItemEditPayload,
): Promise<ItemEditResponse> {
  const encodedReport = encodeURIComponent(reportId);
  const encodedItem = encodeURIComponent(itemId);
  return apiRequest<ItemEditResponse>(
    `/reports/${encodedReport}/items/${encodedItem}`,
    {
      method: 'PATCH',
      body: JSON.stringify(edit),
    }
  );
}

export async function bulkUpdateReportItemsExcluded(
  reportId: string,
  payload: BulkUpdateItemsExcludedPayload,
): Promise<BulkUpdateItemsExcludedResponse> {
  const encodedReport = encodeURIComponent(reportId);
  return apiRequest<BulkUpdateItemsExcludedResponse>(
    `/reports/${encodedReport}/items`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  );
}

/**
 * Signal that all PDFs for a report have uploaded, and start processing.
 *
 * The API lists the PDFs under `private/{identityId}/{reportId}/` in the
 * upload bucket, checks each key belongs to the report's identity, copies
 * them to the organized bucket, flips the report PENDING to PROCESSING,
 * charges one run and async-invokes the Processor. It lists once, with no
 * server-side retry: fewer files than `expectedFileCount` returns 408, and
 * UploadForm retries once.
 *
 * Errors, all before the flip and the charge unless noted: 400 when no PDFs
 * are listed or a key is malformed (not form validation; UploadForm says so),
 * 403 `TERMS_REQUIRED` or a bare 403 on an identity mismatch, 404, 408 as
 * above, 409 when the report has already left PENDING, 429 from the gateway
 * throttle. The exception is a 403 `NO_RUNS` from the charge itself, which
 * comes after the flip and marks the report FAILED.
 *
 * @param reportId  Report id returned by `createReport`.
 * @param expectedFileCount  Number of PDFs the client uploaded.
 */
export async function beginProcessing(
  reportId: string,
  expectedFileCount: number,
): Promise<BeginProcessingResponse> {
  const encodedId = encodeURIComponent(reportId);
  return apiRequest<BeginProcessingResponse>(
    `/reports/${encodedId}/begin-processing`,
    {
      method: 'POST',
      body: JSON.stringify({ expectedFileCount }),
    }
  );
}

/**
 * Create a new Reports row server-side BEFORE uploading PDFs.
 *
 * Reads `identityId` from the current Cognito session and includes it in the
 * body. The server validates against the JWT and returns the generated
 * `reportId` plus the `private/{identityId}/{reportId}/` prefix the frontend
 * should upload PDFs to. PDFs are then uploaded as pure file transfer with
 * no identity-bearing metadata — the row is the source of truth.
 *
 * @param payload Inspection metadata (form fields).
 * @returns `{ reportId, uploadPrefix }` from the server.
 * @throws ApiError on 400 (validation), 409 (collision with another owner), 5xx.
 * @throws ApiError MALFORMED_RESPONSE if the server response is missing
 *   `reportId` or `uploadPrefix` — defends against camelCase/snake_case
 *   contract drift.
 */
export async function createReport(
  payload: CreateReportPayload,
): Promise<CreateReportResponse> {
  const session = await fetchAuthSession();
  const identityId = session.identityId;
  if (!identityId) {
    throw new ApiError('No identity ID in session', HTTP_STATUS.UNAUTHORIZED, 'NO_IDENTITY');
  }
  const body: CreateReportRequestBody = { ...payload, identity_id: identityId };
  const result = await apiRequest<CreateReportResponse>('/reports', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!result.reportId || !result.uploadPrefix) {
    throw new ApiError(
      'Malformed createReport response',
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
      'MALFORMED_RESPONSE',
    );
  }
  return result;
}
